import React, { useEffect, useState } from "react";
import "./App.css";

function App() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);

  const fetchImages = async () => {
    try {
      setError(null);
      const res = await fetch("https://api.data.gov.sg/v1/transport/traffic-images");
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      setImages(data.items[0].cameras);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      console.error("Error fetching traffic images:", err);
      setError("Failed to load traffic images. Please try again later.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages(); // initial call
    const interval = setInterval(fetchImages, 60000); // refresh every 1 min
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    fetchImages();
  };

  const handleCardClick = (camera) => {
    setExpandedCard(camera);
  };

  const handleCloseExpanded = () => {
    setExpandedCard(null);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setExpandedCard(null);
    }
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (expandedCard) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [expandedCard]);

  if (loading && images.length === 0) {
    return (
      <div className="app">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h2>Loading live traffic cameras...</h2>
          <p>Fetching real-time data from Singapore's traffic network</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <button className="retry-button" onClick={handleRefresh}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-title">
            <span className="title-icon">🚦</span>
            Singapore Live Traffic Cameras
          </h1>
          <p className="app-subtitle">
            Real-time traffic monitoring across the island
          </p>
          <div className="header-actions">
            <button className="refresh-button" onClick={handleRefresh} disabled={loading}>
              {loading ? "Refreshing..." : "🔄 Refresh"}
            </button>
            {lastUpdated && (
              <div className="last-updated">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-number">{images.length}</span>
            <span className="stat-label">Active Cameras</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{new Date().toLocaleDateString()}</span>
            <span className="stat-label">Today's Date</span>
          </div>
        </div>

        <div className="cameras-grid">
          {images.map((cam) => (
            <div 
              key={cam.camera_id} 
              className="camera-card"
              onClick={() => handleCardClick(cam)}
            >
              <div className="camera-header">
                <h3 className="camera-title">
                  <span className="camera-icon">📹</span>
                  Camera {cam.camera_id}
                </h3>
              </div>
              
              <div className="image-container">
                <img 
                  src={cam.image} 
                  alt={`Live feed from Camera ${cam.camera_id}`} 
                  className="camera-image"
                  loading="lazy"
                />
                <div className="image-overlay">
                  <div className="image-info">
                    <span>{cam.image_metadata.width} × {cam.image_metadata.height}</span>
                  </div>
                </div>
              </div>

              <div className="camera-footer">
                <div className="camera-details">
                  <span className="detail-item">
                    <strong>Updated:</strong> {new Date(cam.timestamp).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Expanded Card Modal */}
        {expandedCard && (
          <div className="expanded-overlay" onClick={handleOverlayClick}>
            <div className="expanded-modal">
              <div className="expanded-header">
                <h2 className="expanded-title">
                  <span className="camera-icon">📹</span>
                  Camera {expandedCard.camera_id}
                </h2>
                <button className="close-button" onClick={handleCloseExpanded}>
                  ✕
                </button>
              </div>
              
              <div className="expanded-image-container">
                <img 
                  src={expandedCard.image} 
                  alt={`Live feed from Camera ${expandedCard.camera_id}`} 
                  className="expanded-image"
                />
              </div>
              
              <div className="expanded-details">
                <div className="detail-row">
                  <span className="detail-label">Last Updated:</span>
                  <span className="detail-value">
                    {new Date(expandedCard.timestamp).toLocaleString()}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Image Resolution:</span>
                  <span className="detail-value">
                    {expandedCard.image_metadata.width} × {expandedCard.image_metadata.height}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Camera ID:</span>
                  <span className="detail-value">{expandedCard.camera_id}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {images.length === 0 && !loading && (
          <div className="no-data">
            <div className="no-data-icon">📷</div>
            <h3>No cameras available</h3>
            <p>There are currently no traffic cameras showing live feeds.</p>
          </div>
        )}
      </main>

      <footer className="app-footer">
        <p>
          Data provided by <a href="https://data.gov.sg" target="_blank" rel="noopener noreferrer">Data.gov.sg</a> • 
          Powered by LTA's Datamall • Updates every 20 seconds
        </p>
      </footer>
    </div>
  );
}

export default App;
